// =============================================================
// 演習で見せる画像（デッサン・モチーフの写真）の先読み（2026-10-12）
//
// 旧方式は問ごとにその場で読み込んでいたため、
//   ・通信が切れると次の問の画像が出ず、通信が戻っても画面を開き直すまで出なかった
//   ・読み込みが一度失敗（中断）すると「画像がありません」のまま再試行しなかった
// そこで演習を開いた時点で全問ぶんを読み込んでブラウザの中（Blob）に持ち、以後はそこから表示する
// （preloadImages）。読み込みに失敗したら間隔をあけて再試行する。表示側は useCachedImage を使う。
// =============================================================

const entries = new Map(); // url -> { promise, objectUrl }
const RETRY_DELAYS_MS = [500, 1500, 4000];
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Blob で持てない環境（テストなど）では、元の URL のまま表示する
const canCache = () => typeof fetch === 'function' && typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function';

async function fetchOnce(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  // 置き場所を間違えたファイルは index.html（200）が返るので、画像でなければ失敗とみなす
  const type = res.headers.get('content-type') || '';
  if (!/^image\//.test(type)) throw new Error(`画像ではない応答（${type}）`);
  return URL.createObjectURL(await res.blob());
}

async function fetchWithRetry(url) {
  for (let i = 0; ; i++) {
    try {
      return await fetchOnce(url);
    } catch (e) {
      if (i >= RETRY_DELAYS_MS.length) throw e;
      await sleep(RETRY_DELAYS_MS[i]);
    }
  }
}

/** 画像を読み込む（読み込み済みならすぐ）。表示に使う URL で解決する。失敗したら次に呼ばれたときにやり直す。 */
export function loadImage(url) {
  if (!url) return Promise.reject(new Error('url がない'));
  if (!canCache()) return Promise.resolve(url);
  const found = entries.get(url);
  if (found) return found.promise;
  const entry = { objectUrl: null };
  entry.promise = fetchWithRetry(url).then(
    (objectUrl) => { entry.objectUrl = objectUrl; return objectUrl; },
    (err) => { entries.delete(url); throw err; },
  );
  entries.set(url, entry);
  return entry.promise;
}

/** 読み込み済みなら表示に使う URL、まだなら null。 */
export function getLoadedImage(url) {
  if (!url) return null;
  if (!canCache()) return url;
  const e = entries.get(url);
  return (e && e.objectUrl) || null;
}

/** まとめて先に読み込んでおく（結果は待たない。失敗したものは表示するときにまた読み直す）。 */
export function preloadImages(urls) {
  [...new Set(urls)].filter(Boolean).forEach((u) => loadImage(u).catch(() => {}));
}
